import type {
    PlannableTriggerKind,
    VirtualAppointmentSnoozeTrigger,
    VirtualAppointmentWeeksFrom
} from '../../services/virtualAppointments';
import {
    MAX_TRIGGER_ALIGNER,
    MAX_TRIGGER_NOGOS,
    MAX_TRIGGER_WEEKS,
    MIN_TRIGGER_ALIGNER,
    MIN_TRIGGER_NOGOS,
    MIN_TRIGGER_WEEKS
} from '../../services/virtualAppointmentTriggerLimits.ts';
import {
    parseAlignerInput,
    parseNogosInput,
    parseWeeksInput
} from './virtualAppointmentTriggerInput.ts';
import { NO_DM_LINK_HINT } from './virtualAppointmentNogos.ts';
import {
    alignerLimitOf,
    alignerOverLimitError,
    alignerPassedError,
    NO_SAVED_TRIGGERS,
    triggerProgressError
} from './virtualAppointmentTriggerAhead.ts';
import { currentTreatmentWeek } from './virtualAppointmentWeeks.ts';

export interface SnoozeDraft {
    triggerKind: PlannableTriggerKind;
    triggerAligner: string;
    /** Week N, counted from `weeksFrom`: "in N weeks" when that is now. */
    triggerWeeks: string;
    weeksFrom: VirtualAppointmentWeeksFrom;
    /** Absolute threshold: the no-go count restarts at 0 on snooze. */
    triggerNogos: string;
}

export type SnoozeValidation =
    { ok: true; trigger: VirtualAppointmentSnoozeTrigger } | { ok: false; error: string };

// A snooze reads "in N weeks" by default, so a week counts from now unless switched.
export const EMPTY_SNOOZE_DRAFT: SnoozeDraft = {
    triggerKind: 'aligner',
    triggerAligner: '',
    triggerWeeks: '',
    weeksFrom: 'now',
    triggerNogos: ''
};

// A no-go VA snoozes to no-gos again by default, prefilled with its threshold.
export function initialSnoozeDraft(currentNogos: number | null, dmLinked: boolean): SnoozeDraft {
    return currentNogos !== null && dmLinked
        ? { ...EMPTY_SNOOZE_DRAFT, triggerKind: 'nogos', triggerNogos: String(currentNogos) }
        : EMPTY_SNOOZE_DRAFT;
}

export interface SnoozeLimits {
    /** The patient's current aligner; null when unknown, which allows any valid aligner. */
    currentAligner: number | null;
    /** The patient's aligner count; null or absent when unknown, which sets no upper bound. */
    totalAligners?: number | null;
    /** UTC day the Treatment phase began; null rules out counting from it, as the API does. */
    treatmentStartDate: string | null;
    /** UTC today, YYYY-MM-DD. */
    todayIso: string;
    /** Whether the patient is linked to Dental Monitoring; false rules out a no-go snooze. */
    dmLinked: boolean;
}

function validateAligner(raw: string, limits: SnoozeLimits): SnoozeValidation {
    const aligner = parseAlignerInput(raw);
    if (aligner === null) {
        return {
            ok: false,
            error: `Enter an aligner number from ${MIN_TRIGGER_ALIGNER} to ${MAX_TRIGGER_ALIGNER}`
        };
    }
    const error =
        alignerPassedError(aligner, limits.currentAligner) ??
        alignerOverLimitError(aligner, alignerLimitOf(limits.totalAligners));
    if (error) return { ok: false, error };
    return { ok: true, trigger: { triggerKind: 'aligner', triggerAligner: aligner } };
}

function weeksRangeError(weeksFrom: VirtualAppointmentWeeksFrom): string {
    return weeksFrom === 'now'
        ? `Enter a number of weeks from ${MIN_TRIGGER_WEEKS} to ${MAX_TRIGGER_WEEKS}`
        : `Enter a week from ${MIN_TRIGGER_WEEKS} to ${MAX_TRIGGER_WEEKS}`;
}

// "In N weeks" is sent as week N from now; the API stamps the anchor, so no start is needed.
function validateWeeks(draft: SnoozeDraft, limits: SnoozeLimits): SnoozeValidation {
    const weeks = parseWeeksInput(draft.triggerWeeks);
    if (weeks === null) return { ok: false, error: weeksRangeError(draft.weeksFrom) };
    const progress = {
        currentAligner: null,
        currentWeek: currentTreatmentWeek(limits.treatmentStartDate, limits.todayIso),
        alignerLimit: null,
        weeksUnavailable: limits.treatmentStartDate === null
    };
    const parsed = {
        triggerKind: 'weeks' as const,
        aligner: null,
        weeks,
        weeksFrom: draft.weeksFrom
    };
    const error = triggerProgressError(parsed, progress, NO_SAVED_TRIGGERS);
    if (error) return { ok: false, error };
    return {
        ok: true,
        trigger: { triggerKind: 'weeks', triggerWeeks: weeks, weeksFrom: draft.weeksFrom }
    };
}

function validateNogos(raw: string, dmLinked: boolean): SnoozeValidation {
    if (!dmLinked) return { ok: false, error: NO_DM_LINK_HINT };
    const nogos = parseNogosInput(raw);
    if (nogos === null) {
        return {
            ok: false,
            error: `Enter a number of no-gos from ${MIN_TRIGGER_NOGOS} to ${MAX_TRIGGER_NOGOS}`
        };
    }
    return { ok: true, trigger: { triggerKind: 'nogos', triggerNogos: nogos } };
}

// Mirrors the backend snooze rules: an aligner after the current one, a week still ahead, no-gos need DM.
export function validateSnoozeDraft(draft: SnoozeDraft, limits: SnoozeLimits): SnoozeValidation {
    switch (draft.triggerKind) {
        case 'aligner':
            return validateAligner(draft.triggerAligner, limits);
        case 'weeks':
            return validateWeeks(draft, limits);
        case 'nogos':
            return validateNogos(draft.triggerNogos, limits.dmLinked);
    }
}
